import React, { useState } from "react";
import dayjs from "dayjs";
import { Checkbox, FormControlLabel } from "@mui/material";
import Loading from "components/ui/Loading";
import { Heading } from "components/ui/Text";
import { getAdminExperimentsSkipPayroll } from "store/admin/api/apiSlice";
import { camelCaseToTitleCase, formatCurrency } from "utils/string";
import { PartnerSlug, ProductName } from "types/general";
import { colors } from "utils/theme";
import OnboardingFunnel from "../../components/OnboardingFunnel";
import DatePicker from "../../components/DatePicker";
import * as Styled from "./styled";

const BASE_COUNTS = {
  total: 0,
  eligible: 0,
  skippedPayroll: 0,
  linkedPayroll: 0,
  issuedCard: 0,
  cardSpend: 0,
};

const METHODS = ["manual", "automatic"];
const PARTNERS = [PartnerSlug.msufcu, PartnerSlug.csc];

const SkipPayrollTest: React.FC = () => {
  const [createdAtMin, setCreatedAtMin] = useState<string | null>(null);
  const [createdAtMax, setCreatedAtMax] = useState<string | null>(null);
  const [partnersToDisplay, setPartnersToDisplay] = useState(new Set(PARTNERS));
  const { data } = getAdminExperimentsSkipPayroll({
    createdAtMin,
    createdAtMax,
  });

  const onChangePartnerCheckbox = (event) => {
    const newPartnersToDisplay = new Set(partnersToDisplay);

    if (event.target.checked) {
      newPartnersToDisplay.add(event.target.name);
    } else {
      newPartnersToDisplay.delete(event.target.name);
    }

    setPartnersToDisplay(newPartnersToDisplay);
  };

  if (!data) return <Loading />;

  const users = data.users.filter((user) =>
    partnersToDisplay.has(user.partnerSlug)
  );

  const metricsBySkipMethod = users.reduce(
    (obj, user) => {
      // Only users that have ever linked a bank are in the response, so we can assume a user with no method defined will be manual
      const method = user.payrollLinkingSkipMethod || "manual";
      const metrics = { ...obj[method] };
      metrics.total += 1;
      if (user.linkedPayroll) metrics.linkedPayroll += 1;
      if (user.issuedCard) metrics.issuedCard += 1;
      if (user.cardSpend > 0) metrics.cardSpend += user.cardSpend;
      if (user.payrollLinkingSkipMethod) metrics.skippedPayroll += 1;
      if (user.eligible || user.issuedCard) metrics.eligible += 1;

      return {
        ...obj,
        [method]: metrics,
      };
    },
    { manual: { ...BASE_COUNTS }, automatic: { ...BASE_COUNTS } }
  );

  const funnelSeries = [
    {
      dataKey: "manual",
      label: "Manual",
      color: colors.azure,
    },
    {
      dataKey: "automatic",
      label: "Automatic",
      color: colors.violet,
    },
  ];

  const funnelUsers = users.map((user) => ({
    ...user,
    payrollLinkingSkipMethod: user.payrollLinkingSkipMethod || "manual",
  }));

  return (
    <Styled.Wrapper>
      <Heading.H4>Skip Payroll Test</Heading.H4>

      <Styled.Section size={12}>
        <Styled.Options>
          <DatePicker
            label="Start"
            defaultValue={dayjs("2025-04-18")}
            onChange={(value) => setCreatedAtMin(value.format("YYYY-MM-DD"))}
          />
          <DatePicker
            label="End"
            defaultValue={dayjs()}
            onChange={(value) => setCreatedAtMax(value.format("YYYY-MM-DD"))}
          />
        </Styled.Options>
      </Styled.Section>

      <Styled.Section size={12}>
        <Styled.Options>
          {PARTNERS.map((partnerSlug) => (
            <FormControlLabel
              key={partnerSlug}
              label={partnerSlug.toUpperCase()}
              control={
                <Checkbox
                  onChange={onChangePartnerCheckbox}
                  name={partnerSlug}
                  defaultChecked={PARTNERS.includes(partnerSlug)}
                />
              }
            />
          ))}
        </Styled.Options>
      </Styled.Section>

      <Styled.Table $noCollapse>
        <thead>
          <tr>
            <th></th>
            {METHODS.map((method) => (
              <th key={method}>{camelCaseToTitleCase(method)}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {Object.keys(BASE_COUNTS).map((key) => (
            <tr key={key}>
              <th>{camelCaseToTitleCase(key)}</th>
              {METHODS.map((method) => {
                let value = metricsBySkipMethod[method][key];

                if (key === "cardSpend")
                  value = formatCurrency(value / 100, true);

                return <td key={method}>{value}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </Styled.Table>

      <Styled.ChartWrapper>
        <OnboardingFunnel
          productEntryPoint={ProductName.earnedWageAccess}
          series={funnelSeries}
          sortByKey="payrollLinkingSkipMethod"
          users={funnelUsers}
        />
      </Styled.ChartWrapper>
    </Styled.Wrapper>
  );
};

export default SkipPayrollTest;
